import axios from "axios";
import { tokenStorage } from "./client";

const SPEAKING_ORIGIN = (
  process.env.VUE_APP_SPEAKING_ORIGIN || "https://speaking.cocouni.ir"
).replace(/\/$/, "");

const SPEAKING_API_URL = (
  process.env.VUE_APP_SPEAKING_API_URL || SPEAKING_ORIGIN
).replace(/\/$/, "");

export type SpeakingRoomStatus =
  | "waiting"
  | "in_progress"
  | "round_break"
  | "ended";

export interface SpeakingRoom {
  room_code: string;
  status: SpeakingRoomStatus;
  host_name?: string;
  host_user_id?: string;
  examiner_name?: string;
  attendee_name?: string;
  examiner_taken: boolean;
  attendee_taken: boolean;
  joinable: boolean;
  open_role?: "examiner" | "attendee";
  is_mine?: boolean;
}

interface ListRoomsResponse {
  rooms: SpeakingRoom[];
}

interface CreateRoomResponse {
  room_id: string;
  room_code: string;
  join_path: string;
}

interface CreateRoomConflict {
  error?: string;
  room_code?: string;
}

export class SpeakingRoomExistsError extends Error {
  roomCode: string;

  constructor(roomCode: string) {
    super("you already have an active room");
    this.name = "SpeakingRoomExistsError";
    this.roomCode = roomCode;
  }
}

function authHeaders(): Record<string, string> {
  const token = tokenStorage.getAccess();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function speakingLobbyUrl(code: string, role?: string): string {
  const params = new URLSearchParams({ code: code.toUpperCase() });
  if (role === "examiner" || role === "attendee") {
    params.set("role", role);
  }
  return `${SPEAKING_ORIGIN}/?${params.toString()}`;
}

export function openSpeakingLobby(code: string, role?: string): void {
  window.open(speakingLobbyUrl(code, role), "_blank", "noopener,noreferrer");
}

export async function listActiveSpeakingRooms(): Promise<SpeakingRoom[]> {
  const { data } = await axios.get<ListRoomsResponse>(
    `${SPEAKING_API_URL}/api/v1/rooms`,
    { timeout: 15000, headers: authHeaders() }
  );
  return data.rooms ?? [];
}

export async function createSpeakingRoom({
  replaceExisting = false,
}: { replaceExisting?: boolean } = {}): Promise<CreateRoomResponse> {
  try {
    const { data } = await axios.post<CreateRoomResponse>(
      `${SPEAKING_API_URL}/api/v1/rooms`,
      { replace_existing: replaceExisting },
      { timeout: 15000, headers: authHeaders() }
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const body = error.response.data as CreateRoomConflict | undefined;
      if (body?.room_code) {
        throw new SpeakingRoomExistsError(body.room_code);
      }
    }
    throw error;
  }
}

export async function deleteSpeakingRoom(code: string): Promise<void> {
  await axios.delete(
    `${SPEAKING_API_URL}/api/v1/rooms/${encodeURIComponent(code)}`,
    { timeout: 15000, headers: authHeaders() }
  );
}
